import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { Member, MemberRoleScopeType, MemberRoleType } from './model';

const getProfileState = (state: RootState) => state.profile;

export const selectMember = createSelector(
  [getProfileState],
  (profileState) => profileState.member
);

export const selectModeratedRoomsIds = createSelector(
  [getProfileState],
  (profileState) =>
    profileState.member?.roles
      ?.find((role) => role.type === MemberRoleType.moderator)
      ?.scopes.filter((scope) => scope.type === MemberRoleScopeType.room)
      .map((roomScope) => {
        return roomScope.id;
      }) ?? []
);

export const selectIsMemberUnverified = createSelector(
  [getProfileState],
  (profileState) =>
    profileState.member?.verification?.verificationStatus === 'unverified'
);

export const selectIsMemberPendingOrUnverified = createSelector(
  [getProfileState],
  (profileState) =>
    profileState.member?.verification?.verificationStatus === 'unverified' ||
    profileState.member?.verification?.verificationStatus === 'pending'
);

export const selectMemberError = createSelector(
  [getProfileState],
  (profileState) => profileState.memberError
);

export const selectUpdateMemberError = createSelector(
  [getProfileState],
  (profileState) => profileState.updateMemberError
);

export const selectUpdateUserError = createSelector(
  [getProfileState],
  (profileState) => profileState.updateUserError
);

export const selectLoadingMember = createSelector(
  [getProfileState],
  (profileState) => profileState.memberLoading
);

export const selectMembership = createSelector(
  [getProfileState],
  (profileState) => profileState.membership[0]
);

export const selectCarepoints = createSelector(
  [getProfileState],
  (profileState) => profileState.carepoints
);

export const selectCarepointsNumber = createSelector(
  [getProfileState],
  (profileState) => {
    const carepoints = profileState.carepoints;
    if (!carepoints || !carepoints.length) return undefined;

    return carepoints
      .map((carepoints) => carepoints.points)
      .reduce((sum, carepoints) => sum + carepoints);
  }
);

export const selectCarepointsRewards = createSelector(
  [getProfileState],
  (profileState) => {
    return profileState.carepointsRewards;
  }
);

export const selectUser = createSelector(
  [getProfileState],
  (profileState) => profileState.user
);

export const selectIsUpdatingUser = createSelector(
  [getProfileState],
  (profileState) => profileState.isUpdatingUser
);

export const selectIsUpdatingMember = createSelector(
  [getProfileState],
  (profileState) => profileState.isUpdatingMember
);

export const selectAvatars = (currentPage: number, pageSize: number) =>
  createSelector([getProfileState], (profileState) =>
    profileState.avatars.slice(
      currentPage * pageSize,
      currentPage * pageSize + pageSize
    )
  );

export const selectAllAvatars = createSelector(
  [getProfileState],
  (profileState) => profileState.avatars
);

export const selectLoadingAvatars = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingAvatars
);

export const selectNumberOfAvatars = createSelector(
  [getProfileState],
  (profileState) => profileState.avatars.length
);

export const selectMemberInfo = createSelector(
  [getProfileState],
  (profileState) => profileState.memberInfo
);

export const selectAuthorInfo = createSelector(
  [getProfileState],
  (profileState) => profileState.authorInfo
);

export const selectLoadingAuthorInfo = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingAuthorInfo
);

export const selectLoadingFollowUnfollow = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingFollowUnfollow
);

export const selectMyPreferredTopics = createSelector(
  [getProfileState],
  (profileState) => profileState.myPreferredTopics
);

export const selectLoadingMyPreferences = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingPreferences
);

export const selectMyPreferredTopicsError = createSelector(
  [getProfileState],
  (profileState) => profileState.myPreferredTopicsError
);

export const selectUpdateMyTopicsInProgress = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingUpdatePreferences
);

export const selectIsStoriesModerator = createSelector(
  [getProfileState],
  (profileState) =>
    !!(
      profileState.member &&
      profileState.member.roles &&
      profileState.member.roles.filter(
        (role) =>
          role.type === MemberRoleType.moderator &&
          role.scopes.filter(
            (scope) => scope.type === MemberRoleScopeType.stories
          ).length > 0
      ).length > 0
    )
);

export const selectIsApproved = createSelector(
  [getProfileState],
  (profileState) =>
    !!(
      profileState.member &&
      profileState.member.roles &&
      profileState.member.roles.filter(
        (role) => role.type === MemberRoleType.approved
      ).length > 0
    )
);

export const selectIsRoomModerator = (member?: Member, roomId?: string) =>
  createSelector([getProfileState], (profileState) =>
    roomId
      ? !!(
          member &&
          member.roles &&
          member.roles.filter(
            (role) =>
              role.type === MemberRoleType.moderator &&
              role.scopes.filter(
                (scope) =>
                  scope.id === roomId && scope.type === MemberRoleScopeType.room
              ).length > 0
          ).length > 0
        )
      : false
  );

export const selectUserIsRoomsModerator = createSelector(
  [getProfileState],
  (profileState) =>
    profileState.member &&
    profileState.member.roles &&
    profileState.member.roles.filter(
      (role) =>
        role.type === MemberRoleType.moderator &&
        role.scopes.filter((scope) => scope.type === MemberRoleScopeType.room)
          .length > 0
    ).length > 0
);

export const isGlobalAdministrator = (member?: Member) =>
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      !role.scopes?.some(
        (scope) => scope.type === MemberRoleScopeType.surveysInRoom
      )
  );

export const isExactGlobalAdministrator = (member?: Member) =>
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      role.scopes?.some(
        (scope) => scope.type === MemberRoleScopeType.global
      )
  );

export const canMemberAccessSylvia = (
  member?: Member,
  roomId?: string,
  parentRoomId?: string
) =>
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      role.scopes?.some(
        (scope) =>
          scope.type === MemberRoleScopeType.global ||
          (scope.type === MemberRoleScopeType.room &&
            ((!!roomId && scope.id === roomId) ||
              (!!parentRoomId && scope.id === parentRoomId)))
      )
  );

export const canMemberAuthorSurveysInRoom = (
  member?: Member,
  roomId?: string
) =>
  !!roomId &&
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      (!role.scopes?.length ||
        role.scopes.some(
          (scope) =>
            scope.type === MemberRoleScopeType.global ||
            ((scope.type === MemberRoleScopeType.room ||
              scope.type === MemberRoleScopeType.surveysInRoom) &&
              scope.id === roomId)
        ))
  );

export const canMemberDeleteGovernedSurveysInRoom = (
  member?: Member,
  roomId?: string
) =>
  !!roomId &&
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      role.scopes?.some(
        (scope) =>
          scope.type === MemberRoleScopeType.global ||
          ((scope.type === MemberRoleScopeType.room ||
            scope.type === MemberRoleScopeType.surveysInRoom) &&
            scope.id === roomId)
      )
  );

export const canMemberAnalyzeSurveysInRoom = (
  member?: Member,
  roomId?: string
) =>
  !!roomId &&
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      role.scopes?.some(
        (scope) =>
          scope.type === MemberRoleScopeType.global ||
          ((scope.type === MemberRoleScopeType.room ||
            scope.type === MemberRoleScopeType.surveysInRoom) &&
            scope.id === roomId)
      )
  );

export const canMemberExportSurveyAnalysisInRoom = (
  member?: Member,
  roomId?: string
) =>
  !!roomId &&
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      role.scopes?.some(
        (scope) =>
          scope.type === MemberRoleScopeType.global ||
          (scope.type === MemberRoleScopeType.surveysInRoom &&
            scope.id === roomId)
      )
  );

export const canMemberManageAISettingsInRoom = (
  member?: Member,
  roomId?: string
) =>
  !!roomId &&
  !!member?.roles?.some(
    (role) =>
      role?.type === MemberRoleType.administrator &&
      (!role.scopes?.length ||
        role.scopes.some(
          (scope) =>
            scope.type === MemberRoleScopeType.global ||
            ((scope.type === MemberRoleScopeType.room ||
              scope.type === MemberRoleScopeType.surveysInRoom) &&
              scope.id === roomId)
        ))
  );

export const selectIsCurrentUserAdministrator = createSelector(
  [getProfileState],
  (profileState) => isGlobalAdministrator(profileState.member)
);

export const selectCanCurrentUserAuthorSurveysInRoom = (roomId?: string) =>
  createSelector([getProfileState], (profileState) =>
    canMemberAuthorSurveysInRoom(profileState.member, roomId)
  );

export const selectCanCurrentUserDeleteGovernedSurveysInRoom = (
  roomId?: string
) =>
  createSelector([getProfileState], (profileState) =>
    canMemberDeleteGovernedSurveysInRoom(profileState.member, roomId)
  );

export const selectCanCurrentUserAnalyzeSurveysInRoom = (roomId?: string) =>
  createSelector([getProfileState], (profileState) =>
    canMemberAnalyzeSurveysInRoom(profileState.member, roomId)
  );

export const selectCanCurrentUserExportSurveyAnalysisInRoom = (
  roomId?: string
) =>
  createSelector([getProfileState], (profileState) =>
    canMemberExportSurveyAnalysisInRoom(profileState.member, roomId)
  );

export const selectCanCurrentUserManageAISettingsInRoom = (roomId?: string) =>
  createSelector([getProfileState], (profileState) =>
    canMemberManageAISettingsInRoom(profileState.member, roomId)
  );

export const canMemberManageHubConfiguration = (member?: Member, roomId?: string) =>
  !!roomId && !!member?.roles?.some(role =>
    (role.type === MemberRoleType.administrator && role.scopes?.some(scope =>
      scope.type === MemberRoleScopeType.global ||
      (scope.type === MemberRoleScopeType.room && scope.id === roomId))));

export const selectCanCurrentUserManageHubConfiguration = (roomId?: string) =>
  createSelector([getProfileState], state => canMemberManageHubConfiguration(state.member, roomId));

export const selectAboutYouData = createSelector(
  [getProfileState],
  (profileState) => profileState.aboutYouData
);

export const selectBlockedMember = (memberId: string) =>
  createSelector([getProfileState], (profileState) =>
    profileState.blockedMembers?.find((member) => member.memberId === memberId)
  );

export const selectReferralLink = createSelector(
  [getProfileState],
  (profileState) => profileState.referralLink
);

export const selectLoadingSendingEmailsInvite = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingSendingEmailsInvite
);

export const selectEmailsSendingError = createSelector(
  [getProfileState],
  (profileState) => profileState.emailsSendingError
);

export const selectMyPreferences = createSelector(
  [getProfileState],
  (profileState) => profileState.myPreferences
);

export const selectLoadingPreferences = createSelector(
  [getProfileState],
  (profileState) => profileState.loadingMyPreferences
);

export const selectMyPreferencesError = createSelector(
  [getProfileState],
  (profileState) => profileState.myPreferencesError
);

export const selectIsEmailVerificationSuccessful = createSelector(
  [getProfileState],
  (profileState) => profileState.isEmailVerificationSuccessful
);

export const selectIsEmailVerificationError = createSelector(
  [getProfileState],
  (profileState) => profileState.emailVerficationError
);

export const selectRelationshipTypes = createSelector(
  [getProfileState],
  (profileState) => profileState.relationshipTypes
);
